import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { memo } from "react";
import { useUsersContext } from "../context";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module, NO_DATA, PATH_CONTRACTOR } from "@/constants";
import UserUpdateForm from "./UserUpdateForm";
import UserBadge from "./UserBadge";
import { DetailsButton } from "@/components/DetailsButton";
import { Section } from "@/components/details";
import { readUser } from "@/schemas/user";

const UserDetails = memo(() => {
  const { t } = useTranslation();
  const { data: user } = useUsersContext();
  const { can_update } = useModulePermissions(Module.Users);
  const contractorsPermissions = useModulePermissions(Module.Contractors);

  if (!user) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("users.details.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Section
          variant="card"
          className="mb-6"
          module={Module.Users}
          schema={readUser}
          data={user}
        >
          <Section.Field field="ref_number" />
          <Section.Field field="first_name" />
          <Section.Field field="last_name" />
          <Section.Field field="email" />
          <Section.Field field="phone_number" />
          <Section.Field field="system_role_name" />
          <Section.Field
            field="is_active"
            value={
              <Badge variant={user.is_active ? "secondary" : "destructive"}>
                {user.is_active
                  ? t("users.status.active")
                  : t("users.status.inactive")}
              </Badge>
            }
          />
          <Section.Field
            field="preferred_language_iso_code"
            labelKey="users.fields.preferredLanguage"
            value={t(
              `language.${user.preferred_language_iso_code}.name`,
              NO_DATA,
            )}
          />
          <Section.Field
            field="cognito_id"
            value={<span className="font-mono">{user.cognito_id}</span>}
          />
          {user.contractor_id && (
            <Section.Field
              field="contractor_is_active"
              value={<UserBadge isActive={user.contractor_is_active} />}
            />
          )}
          {user.contractor_id && contractorsPermissions.can_read && (
            <Section.Field
              field="contractor_id"
              label={t("users.details.contractorCard")}
              value={
                <DetailsButton
                  path={PATH_CONTRACTOR}
                  pathParams={{ contractorId: user.contractor_id }}
                  label="users.details.contractorDetailsButton"
                />
              }
            />
          )}
        </Section>

        {can_update && (
          <>
            <Separator className="my-6" />
            <UserUpdateForm />
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default UserDetails;
